import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronUpDownIcon
} from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../constants/track-events'
import { useAivyContext, useUserContext } from '../context'
import { classNames } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { isSpaceWithoutPayment } from '../helpers/spaces-without-payment'
import { switchActiveSpace } from '../helpers/switch-active-space'

// Ohne Logo (oder wenn es nicht lädt) der Anfangsbuchstabe, statt eines
// leeren Kastens
const SpaceLogo = ({ partner, className }) => {
  const [hasError, setHasError] = useState(false)

  if (!partner.logo || hasError) {
    return (
      <span
        className={classNames(
          'flex items-center justify-center bg-gray-200 text-sm font-semibold uppercase text-gray-600',
          className
        )}
        aria-hidden='true'
      >
        {(partner.name || '?').charAt(0)}
      </span>
    )
  }

  return (
    <img
      className={classNames('bg-white object-contain', className)}
      src={partner.logo}
      alt=''
      onError={() => setHasError(true)}
    />
  )
}

SpaceLogo.propTypes = {
  partner: PropTypes.object.isRequired,
  className: PropTypes.string
}

const SpaceSwitch = ({ isSidebarCollapsed }) => {
  const { t } = useTranslation()

  const {
    user,
    employees,
    activeSpace: { authorization_role }
  } = useUserContext()

  const { restartDashboard } = useAivyContext()

  // Bis restartDashboard greift, vergeht spürbar Zeit. Ohne Rückmeldung
  // klicken Nutzer sonst ein zweites Mal.
  const [switchingTo, setSwitchingTo] = useState(null)

  const selected = employees.find(
    ({ id: space_id }) => user.active_space_id === space_id
  )

  const handleOnClick = ({ space_id }) => {
    trackEvent(trackEvents.CLICK_CHANGE_LOCATION)
    setSwitchingTo(space_id)

    switchActiveSpace({ userId: user.id, spaceId: space_id })
      .then(restartDashboard)
      .catch((error) => {
        setSwitchingTo(null)
        throw error
      })
  }

  if (!selected) return null

  // Aktueller Standort zuerst, der Rest in der gelieferten Reihenfolge. Nicht
  // per sort, das würde das Array aus dem Context verändern.
  const spaces = [
    selected,
    ...employees.filter(({ id: space_id }) => space_id !== selected.id)
  ]

  return (
    <Popover className='relative'>
      <PopoverButton
        className={classNames(
          'group flex w-full items-center gap-x-2.5 overflow-hidden rounded-md p-1 text-left ring-1 ring-inset ring-white/10 transition-colors',
          'hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brightBlue data-[open]:bg-white/10'
        )}
      >
        <SpaceLogo
          partner={selected.partner}
          className='size-8 shrink-0 rounded'
        />
        <span
          className={classNames(
            'flex min-w-0 flex-1 flex-col transition-opacity duration-200 motion-reduce:transition-none',
            isSidebarCollapsed && 'opacity-0'
          )}
        >
          <span className='truncate text-sm/4 font-medium text-white'>
            {selected.partner.name}
          </span>
          <span className='truncate text-xs/4 text-gray-400'>
            {t(authorization_role.title)}
          </span>
        </span>
        <ChevronUpDownIcon
          className={classNames(
            'size-4 shrink-0 text-gray-400 transition-opacity group-hover:text-gray-300',
            isSidebarCollapsed && 'opacity-0'
          )}
          aria-hidden='true'
        />
      </PopoverButton>

      <PopoverPanel
        transition
        anchor={
          isSidebarCollapsed
            ? { to: 'right start', gap: 20 }
            : { to: 'bottom start', gap: 4 }
        }
        className={classNames(
          'z-50 flex max-h-[min(24rem,var(--anchor-max-height))] flex-col gap-y-0.5 overflow-y-auto rounded-lg bg-white p-1 shadow-lg ring-1 ring-gray-900/5 focus:outline-none',
          'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
          isSidebarCollapsed
            ? 'w-72 origin-top-left'
            : 'w-[var(--button-width)] origin-top'
        )}
      >
        <div className='px-2 pb-1 pt-1.5 text-xs font-medium text-gray-500'>
          {t('components.layout.companys_area_title')}
        </div>
        {spaces.map((space) => {
          const { id: space_id, partner } = space
          const current = selected.id === space_id

          // Space, der beim letzten Besuch in den AccessDenied-Screen
          // geführt hat. Bleibt sichtbar und anwählbar. Die Markierung ist
          // nur eine Beobachtung und kann veraltet sein, ein inzwischen
          // bezahlter Standort darf den User nicht aussperren. Besucht der
          // User den Space und er liefert wieder Payments, verschwindet sie
          // von selbst, und AccessDenied bietet den Rückweg. Der
          // Payment-Status fremder Spaces ist client-seitig nicht
          // abfragbar, siehe helpers/spaces-without-payment.
          const withoutAccess = !current && isSpaceWithoutPayment(space_id)
          const disabled = current || !!switchingTo

          return (
            <button
              key={space_id}
              type='button'
              disabled={disabled}
              onClick={() => handleOnClick({ space_id })}
              aria-current={current ? 'true' : undefined}
              title={partner.name}
              className={classNames(
                'flex w-full items-center gap-x-2.5 rounded-md p-1.5 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue',
                current && 'bg-gray-50',
                !disabled && 'hover:bg-gray-100',
                !!switchingTo && !current && 'cursor-wait'
              )}
            >
              <SpaceLogo
                partner={partner}
                className={classNames(
                  'size-8 shrink-0 rounded ring-1 ring-gray-200',
                  withoutAccess && 'opacity-40 grayscale'
                )}
              />
              <span className='flex min-w-0 flex-1 flex-col'>
                <span
                  className={classNames(
                    'truncate text-sm/4 font-medium',
                    withoutAccess ? 'text-gray-500' : 'text-gray-900'
                  )}
                >
                  {partner.name}
                </span>
                <span className='truncate text-xs/4 text-gray-500'>
                  {withoutAccess
                    ? t('components.space_switch.no_access')
                    : partner.display_name}
                </span>
              </span>
              {current && (
                <CheckIcon
                  className='size-4 shrink-0 text-gray-700'
                  aria-hidden='true'
                />
              )}
              {switchingTo === space_id && (
                <ArrowPathIcon
                  className='size-4 shrink-0 animate-spin text-gray-500'
                  aria-hidden='true'
                />
              )}
            </button>
          )
        })}
      </PopoverPanel>
    </Popover>
  )
}

SpaceSwitch.propTypes = { isSidebarCollapsed: PropTypes.bool.isRequired }

export default SpaceSwitch
